import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, CheckIcon, CalendarIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SOLUTIONS, CATEGORIES } from '../data/painSolutions';
const CALENDLY_URL = 'https://calendly.com/meelo_demo';
type MediaItem = {
  type: 'image' | 'video';
  src: string;
};
function MobileMockup({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="relative mx-auto w-[280px] sm:w-[320px] h-[600px] sm:h-[680px] bg-black rounded-[40px] sm:rounded-[48px] border-[8px] sm:border-[12px] border-black shadow-2xl overflow-hidden flex-shrink-0" data-id="element-1037">
      <div className="absolute top-0 inset-x-0 h-6 bg-black rounded-b-3xl w-1/2 mx-auto z-20" data-id="element-1038"></div>
      <div className="w-full h-full bg-white rounded-[32px] sm:rounded-[36px] overflow-hidden relative z-10" data-id="element-1039">
        {children}
      </div>
    </div>;
}
// ─── Back-to-category handler ───────────────────────────────────────────
function goBackToCategory(e: React.MouseEvent) {
  e.preventDefault();
  if (window.history.length > 1) {
    window.history.back();
  } else {
    window.location.hash = '#/find-solution';
  }
}
// ─── Sticky back bar — persistent navigation while scrolling ────────────
function StickyBackBar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="sticky top-[76px] sm:top-[84px] z-40" style={{
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: scrolled ? '1px solid rgba(0,0,0,0.06)' : '1px solid transparent',
    boxShadow: scrolled ? '0 4px 16px -8px rgba(0,0,0,0.10)' : '0 0 0 transparent',
    transition: 'box-shadow 200ms ease, border-color 200ms ease'
  }} data-id="element-1040">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3" data-id="element-1041">
        <button type="button" onClick={goBackToCategory} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7C4DFF] hover:text-[#651FFF] transition-colors" data-id="element-1042">
          <ArrowLeftIcon size={15} strokeWidth={2.2} data-id="element-1043" />К списку сценариев
        </button>
      </div>
    </div>;
}
// ─── Lightbox / Gallery overlay ─────────────────────────────────────────
function Lightbox({
  items,
  index,
  onClose,
  onPrev,
  onNext,
  onJump
}: {
  items: MediaItem[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onJump: (i: number) => void;
}) {
  const current = items[index];
  const hasMultiple = items.length > 1;
  // Keyboard nav + scroll lock
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();else if (e.key === 'ArrowLeft' && hasMultiple) onPrev();else if (e.key === 'ArrowRight' && hasMultiple) onNext();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext, hasMultiple]);
  if (!current) return null;
  return <div role="dialog" aria-modal="true" className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" style={{
    animation: 'fadeIn 150ms ease-out'
  }} onClick={onClose} data-id="element-1044">
      {/* Close */}
      <button type="button" onClick={e => {
      e.stopPropagation();
      onClose();
    }} aria-label="Закрыть" className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-20 shadow-lg" data-id="element-1045">
        <XIcon size={22} strokeWidth={2.2} data-id="element-1046" />
      </button>

      {/* Counter */}
      {hasMultiple && <div className="absolute top-4 left-1/2 -translate-x-1/2 sm:top-6 text-white text-sm font-semibold bg-black/60 rounded-full px-3.5 py-1.5 z-20 shadow-lg tabular-nums" data-id="element-1047">
          {index + 1} / {items.length}
        </div>}

      {/* Prev */}
      {hasMultiple && <button type="button" onClick={e => {
      e.stopPropagation();
      onPrev();
    }} aria-label="Предыдущий" className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-20 shadow-lg" data-id="element-1048">
          <ChevronLeftIcon size={28} strokeWidth={2.4} data-id="element-1049" />
        </button>}

      {/* Next */}
      {hasMultiple && <button type="button" onClick={e => {
      e.stopPropagation();
      onNext();
    }} aria-label="Следующий" className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-20 shadow-lg" data-id="element-1050">
          <ChevronRightIcon size={28} strokeWidth={2.4} data-id="element-1051" />
        </button>}

      {/* Media — fills viewport, object-contain preserves aspect.
                        Landscape images fill width on phones (with letterboxing);
                        portrait images fill height. Pinch-zoom remains available. */}
      <div className="w-screen h-screen flex items-center justify-center px-2 sm:px-4 py-16" onClick={e => e.stopPropagation()} data-id="element-1052">
        {current.type === 'image' ? <img key={current.src} src={current.src} alt="" className="max-w-full max-h-full w-auto h-auto object-contain select-none" style={{
        touchAction: 'pinch-zoom'
      }} draggable={false} data-id="element-1053" /> : <video key={current.src} src={current.src} controls autoPlay muted playsInline className="max-w-full max-h-full w-auto h-auto rounded-lg bg-black" data-id="element-1054" />}
      </div>

      {/* Dot indicators */}
      {hasMultiple && <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/60 rounded-full px-3 py-2 z-20 shadow-lg" onClick={e => e.stopPropagation()} data-id="element-1055">
          {items.map((_, i) => <button key={i} type="button" onClick={() => onJump(i)} aria-label={`Перейти к ${i + 1}`} className="transition-all" style={{
        width: i === index ? 18 : 6,
        height: 6,
        borderRadius: 999,
        background: i === index ? 'white' : 'rgba(255,255,255,0.4)'
      }} data-id="element-1056" />)}
        </div>}
    </div>;
}
export function PainSolutionPage({
  painId
}: {
  painId: string;
}) {
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo({
      top: 0,
      behavior: 'instant' as ScrollBehavior
    });
    requestAnimationFrame(() => {
      html.style.scrollBehavior = prev;
    });
  }, [painId]);
  const solution = SOLUTIONS[painId];
  // Build a flat ordered media list for the lightbox
  const mediaItems = useMemo<MediaItem[]>(() => {
    if (!solution) return [];
    const items: MediaItem[] = [];
    const ws = solution.whatYouSee;
    if (ws.video) items.push({
      type: 'video',
      src: ws.video
    });
    if (ws.image) items.push({
      type: 'image',
      src: ws.image
    });
    if (ws.images) ws.images.forEach(src => items.push({
      type: 'image',
      src
    }));
    if (solution.howItWorksVideo) items.push({
      type: 'video',
      src: solution.howItWorksVideo
    });
    solution.steps.forEach(step => {
      if (step.image) items.push({
        type: 'image',
        src: step.image
      });
      if (step.images) step.images.forEach(src => items.push({
        type: 'image',
        src
      }));
    });
    return items;
  }, [solution]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = useCallback((src: string) => {
    const idx = mediaItems.findIndex(m => m.src === src);
    if (idx >= 0) setLightboxIndex(idx);
  }, [mediaItems]);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() => {
    setLightboxIndex(i => i === null ? null : (i - 1 + mediaItems.length) % mediaItems.length);
  }, [mediaItems.length]);
  const next = useCallback(() => {
    setLightboxIndex(i => i === null ? null : (i + 1) % mediaItems.length);
  }, [mediaItems.length]);
  const jump = useCallback((i: number) => {
    if (i >= 0 && i < mediaItems.length) setLightboxIndex(i);
  }, [mediaItems.length]);
  if (!solution) {
    return <ComingSoonPage painId={painId} data-id="element-1057" />;
  }
  return <div className="min-h-screen bg-[#FAFAFA]" style={{
    animation: 'fadeIn 200ms ease-out'
  }} data-id="element-1058">
      <Navbar data-id="element-1059" />
      <StickyBackBar data-id="element-1060" />

      {/* ─── 1. Recognition hero ─────────────────────────────────────────── */}
      <section className="relative pt-10 sm:pt-14 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden" data-id="element-1061">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(124,77,255,0.12) 0%, transparent 65%)'
      }} data-id="element-1062" />

        <div className="relative max-w-4xl mx-auto" data-id="element-1063">
          {/* Category badge */}
          <div className="flex items-center gap-2 mb-5" data-id="element-1064">
            <span className="text-2xl" data-id="element-1065">{solution.categoryIcon}</span>
            <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#7C4DFF] bg-[#EDE7F6] px-3 py-1.5 rounded-full" data-id="element-1066">
              {solution.categoryTitle}
            </span>
          </div>

          {/* Pain as question */}
          <h1 className="sm:text-4xl lg:text-[48px] text-gray-900 tracking-tight mb-5 text-[30px] font-[700]" data-id="element-1067">
            {solution.headline}
          </h1>
          {solution.subheadline && <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl" data-id="element-1068">
              {solution.subheadline}
            </p>}
        </div>
      </section>

      {/* ─── 2. What you see in Meelo ────────────────────────────────────── */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-1069">
        <div className="max-w-5xl mx-auto" data-id="element-1070">
          <div className="text-center mb-10" data-id="element-1071">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-3" data-id="element-1072">
              Что вы увидите в Meelo
            </h2>
          </div>

          {solution.whatYouSee.isMobile ? <div className="py-4" data-id="element-1073">
              {solution.whatYouSee.images ? <div className="flex flex-wrap justify-center gap-6 sm:gap-8" data-id="element-1074">
                  {solution.whatYouSee.images.map((img, idx) => <MobileMockup key={idx} data-id="element-1075">
                      <ClickableImage src={img} alt={`Скриншот Meelo ${idx + 1}`} onOpen={openLightbox} className="w-full h-full object-cover block" data-id="element-1076" />
                    </MobileMockup>)}
                </div> : <MobileMockup data-id="element-1077">
                  {solution.whatYouSee.video ? <ClickableVideo src={solution.whatYouSee.video} onOpen={openLightbox} className="w-full h-full object-cover block" data-id="element-1078" /> : solution.whatYouSee.image ? <ClickableImage src={solution.whatYouSee.image} alt="Скриншот Meelo" onOpen={openLightbox} className="w-full h-full object-cover block" data-id="element-1079" /> : null}
                </MobileMockup>}
            </div> : solution.whatYouSee.images ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1080">
              {solution.whatYouSee.images.map((img, idx) => <ZoomableFrame key={idx} data-id="element-1081">
                  <div className="h-7 bg-gray-50 border-b border-gray-100 flex items-center px-3 gap-1.5" data-id="element-1082">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-1083" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-1084" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-1085" />
                  </div>
                  <ClickableImage src={img} alt={`Скриншот Meelo ${idx + 1}`} onOpen={openLightbox} className="w-full h-auto block" data-id="element-1086" />
                </ZoomableFrame>)}
            </div> : <ZoomableFrame rounded="3xl" tall data-id="element-1087">
              <div className="h-8 bg-gray-50 border-b border-gray-100 flex items-center px-4 gap-1.5" data-id="element-1088">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-1089" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-1090" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-1091" />
                <span className="ml-3 text-[10px] text-gray-400 font-medium" data-id="element-1092">
                  meelo.kz
                </span>
              </div>
              {solution.whatYouSee.video ? <ClickableVideo src={solution.whatYouSee.video} onOpen={openLightbox} className="w-full h-auto block" data-id="element-1093" /> : solution.whatYouSee.image ? <ClickableImage src={solution.whatYouSee.image} alt="Скриншот Meelo" onOpen={openLightbox} className="w-full h-auto block" data-id="element-1094" /> : null}
            </ZoomableFrame>}

          <div className="text-center mt-10" data-id="element-1095">
            <p className="text-xl sm:text-2xl font-semibold text-gray-900 max-w-3xl mx-auto leading-snug" data-id="element-1096">
              {solution.whatYouSee.description}
            </p>
            {solution.whatYouSee.features && <ul className="mt-8 flex flex-col gap-3 max-w-2xl mx-auto text-left" data-id="element-1097">
                {solution.whatYouSee.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3 bg-[#FAFAFA] border border-gray-100 rounded-xl p-4" data-id="element-1098">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EDE7F6] flex items-center justify-center mt-0.5" data-id="element-1099">
                      <CheckIcon size={14} className="text-[#7C4DFF]" strokeWidth={3} data-id="element-1100" />
                    </div>
                    <span className="text-gray-800 text-[15px] sm:text-base leading-relaxed font-medium" data-id="element-1101">
                      {feature}
                    </span>
                  </li>)}
              </ul>}
          </div>
        </div>
      </section>

      {/* ─── 3. How it works ─────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-[#FAFAFA]" data-id="element-1102">
        <div className="max-w-5xl mx-auto" data-id="element-1103">
          <div className="text-center mb-12" data-id="element-1104">
            <span className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase text-[#7C4DFF] mb-3" data-id="element-1105">
              Как это работает
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" data-id="element-1106"></h2>
          </div>

          {solution.howItWorksVideo ? <div className="flex flex-col gap-10" data-id="element-1107">
              {/* Video */}
              <ZoomableFrame rounded="3xl" tall maxWidth data-id="element-1108">
                <div className="h-8 bg-gray-50 border-b border-gray-100 flex items-center px-4 gap-1.5" data-id="element-1109">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-1110" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-1111" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-1112" />
                  <span className="ml-3 text-[10px] text-gray-400 font-medium" data-id="element-1113">
                    meelo.kz — Как это работает
                  </span>
                </div>
                <ClickableVideo src={solution.howItWorksVideo} onOpen={openLightbox} className="w-full h-auto block" data-id="element-1114" />
              </ZoomableFrame>

              {/* Steps as text below */}
              <div className={`grid grid-cols-1 gap-6 ${solution.steps.length === 1 ? 'md:grid-cols-1 max-w-4xl mx-auto' : 'md:grid-cols-3'}`} data-id="element-1115">
                {solution.steps.map((step, i) => <div key={i} className="bg-white rounded-3xl border border-gray-100 p-6 sm:p-8 shadow-sm flex flex-col items-center text-center" data-id="element-1116">
                    {solution.steps.length > 1 && <div className="flex items-center gap-3 mb-4" data-id="element-1117">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{
                  background: 'linear-gradient(135deg, #7C4DFF 0%, #651FFF 100%)',
                  boxShadow: '0 4px 12px -2px rgba(124,77,255,0.40)'
                }} data-id="element-1118">
                          {i + 1}
                        </div>
                        <span className="text-xs font-bold tracking-widest uppercase text-gray-400" data-id="element-1119">
                          Шаг {i + 1}
                        </span>
                      </div>}
                    {step.title && <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 leading-tight" data-id="element-1120">
                        {step.title}
                      </h3>}
                    <p className="text-[15px] sm:text-base text-gray-600 leading-relaxed mb-8" data-id="element-1121">
                      {step.description}
                    </p>

                    <div className="flex flex-wrap justify-center gap-6 w-full" data-id="element-1122">
                      {(step.images || (step.image ? [step.image] : [])).map((img, imgIdx) => step.isMobile ? <MobileMockup key={imgIdx} data-id="element-1123">
                              <ClickableImage src={img} alt="" onOpen={openLightbox} className="w-full h-full object-cover block" data-id="element-1124" />
                            </MobileMockup> : <ZoomableFrame key={imgIdx} rounded="2xl" compact data-id="element-1125">
                              <ClickableImage src={img} alt="" onOpen={openLightbox} className="w-full max-h-[500px] object-contain block" data-id="element-1126" />
                            </ZoomableFrame>)}
                      {step.video && (step.isMobile ? <MobileMockup data-id="element-1127">
                            <ClickableVideo src={step.video} onOpen={openLightbox} className="w-full h-full object-cover block" data-id="element-1128" />
                          </MobileMockup> : <ZoomableFrame rounded="2xl" compact data-id="element-1129">
                            <ClickableVideo src={step.video} onOpen={openLightbox} className="w-full max-h-[500px] object-contain block" data-id="element-1130" />
                          </ZoomableFrame>)}
                    </div>
                  </div>)}
              </div>
            </div> : <div className="flex flex-col gap-8 sm:gap-10" data-id="element-1131">
              {solution.steps.map((step, i) => {
            const stepImages = step.images || (step.image ? [step.image] : []);
            const hasMedia = stepImages.length > 0 || !!step.video;
            return <div key={i} className={`bg-white rounded-3xl border border-gray-100 p-5 sm:p-7 lg:p-10 shadow-sm ${hasMedia ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center' : ''}`} data-id="element-1132">
                    <div className={hasMedia && i % 2 === 1 ? 'lg:order-2' : ''} data-id="element-1133">
                      {solution.steps.length > 1 && <div className="flex items-center gap-3 mb-3" data-id="element-1134">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{
                    background: 'linear-gradient(135deg, #7C4DFF 0%, #651FFF 100%)',
                    boxShadow: '0 4px 12px -2px rgba(124,77,255,0.40)'
                  }} data-id="element-1135">
                            {i + 1}
                          </div>
                          <span className="text-xs font-bold tracking-widest uppercase text-gray-400" data-id="element-1136">
                            Шаг {i + 1}
                          </span>
                        </div>}
                      {step.title && <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight" data-id="element-1137">
                          {step.title}
                        </h3>}
                      <p className="text-[15px] text-gray-600 leading-relaxed" data-id="element-1138">
                        {step.description}
                      </p>
                    </div>

                    {hasMedia && <div className={`flex flex-wrap justify-center gap-6 ${i % 2 === 1 ? 'lg:order-1' : ''}`} data-id="element-1139">
                        {stepImages.map((img, imgIdx) => step.isMobile ? <MobileMockup key={imgIdx} data-id="element-1140">
                              <ClickableImage src={img} alt="" onOpen={openLightbox} className="w-full h-full object-cover block" data-id="element-1141" />
                            </MobileMockup> : <ZoomableFrame key={imgIdx} rounded="2xl" compact data-id="element-1142">
                              <ClickableImage src={img} alt="" onOpen={openLightbox} className="w-full max-h-[500px] object-contain block" data-id="element-1143" />
                            </ZoomableFrame>)}
                        {step.video && (step.isMobile ? <MobileMockup data-id="element-1144">
                              <ClickableVideo src={step.video} onOpen={openLightbox} className="w-full h-full object-cover block" data-id="element-1145" />
                            </MobileMockup> : <ZoomableFrame rounded="2xl" compact data-id="element-1146">
                              <ClickableVideo src={step.video} onOpen={openLightbox} className="w-full max-h-[500px] object-contain block" data-id="element-1147" />
                            </ZoomableFrame>)}
                      </div>}
                  </div>;
          })}
            </div>}
        </div>
      </section>

      {/* ─── 4. What changes for you ─────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-1148">
        <div className="max-w-4xl mx-auto" data-id="element-1149">
          <div className="text-center mb-10" data-id="element-1150">
            <span className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase text-[#7C4DFF] mb-3" data-id="element-1151">
              Что меняется для вас
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" data-id="element-1152">
              Результат, который видно сразу
            </h2>
          </div>

          <ul className="flex flex-col gap-3 max-w-2xl mx-auto" data-id="element-1153">
            {solution.outcomes.map((outcome, i) => <li key={i} className="flex items-start gap-4 bg-[#F5F0FF] border border-[#E9DDFF] rounded-2xl px-5 py-4" data-id="element-1154">
                <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white mt-0.5" style={{
              background: 'linear-gradient(135deg, #7C4DFF 0%, #651FFF 100%)'
            }} data-id="element-1155">
                  <CheckIcon size={15} strokeWidth={3} data-id="element-1156" />
                </div>
                <p className="flex-1 text-[15px] sm:text-base font-medium text-gray-900 leading-relaxed" data-id="element-1157">
                  {outcome}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      {/* ─── 5. CTA ──────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-[#FAFAFA]" data-id="element-1158">
        <div className="max-w-3xl mx-auto" data-id="element-1159">
          <div className="relative rounded-3xl overflow-hidden p-8 sm:p-12 text-center" style={{
          background: 'linear-gradient(135deg, #7C4DFF 0%, #651FFF 50%, #4A1FB8 100%)'
        }} data-id="element-1160">
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(255,255,255,0.18) 0%, transparent 60%)'
          }} data-id="element-1161" />

            <div className="relative" data-id="element-1162">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight mb-3 leading-tight" data-id="element-1163">
                Покажем, как это работает{' '}
                <span className="block" data-id="element-1164">в вашем садике</span>
              </h2>
              <p className="text-sm sm:text-base text-white/80 mb-8 max-w-lg mx-auto" data-id="element-1165">
                Запишитесь на 30-минутное демо — пройдёмся именно по вашему
                сценарию и ответим на вопросы
              </p>

              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 bg-white text-[#7C4DFF] font-semibold text-base sm:text-lg px-7 sm:px-8 py-3.5 sm:py-4 rounded-full shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200" data-id="element-1166">
                <CalendarIcon size={18} strokeWidth={2.2} data-id="element-1167" />
                Запросить демо под мой садик
              </a>

              <p className="text-xs text-white/60 mt-4" data-id="element-1168">
                Бесплатно · Без обязательств · 30 минут
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer data-id="element-1169" />

      {/* ─── Lightbox / gallery ─────────────────────────────────────────── */}
      {lightboxIndex !== null && mediaItems.length > 0 && <Lightbox items={mediaItems} index={lightboxIndex} onClose={closeLightbox} onPrev={prev} onNext={next} onJump={jump} data-id="element-1170" />}
    </div>;
}
// ─── Zoomable frame — wraps landscape/desktop screenshots with a
//     subtle "tap to zoom" hint badge. Used only for non-mobile inline media.
function ZoomableFrame({
  children,
  rounded = '2xl',
  tall = false,
  compact = false,
  maxWidth = false
}: {
  children: React.ReactNode;
  rounded?: '2xl' | '3xl';
  tall?: boolean;
  compact?: boolean;
  maxWidth?: boolean;
}) {
  const roundedClass = rounded === '3xl' ? 'rounded-none sm:rounded-3xl' : 'rounded-none sm:rounded-2xl';
  const shadow = tall ? 'shadow-[0_20px_60px_-20px_rgba(124,77,255,0.25)]' : compact ? 'shadow-sm' : 'shadow-lg';
  const width = maxWidth ? '-mx-4 sm:mx-auto max-w-4xl w-auto sm:w-full' : '-mx-4 sm:mx-0';
  return <div className={`group relative ${width} ${roundedClass} overflow-hidden border-y sm:border border-gray-100 ${shadow} bg-white`} data-id="element-1171">
      {children}
      {/* Zoom hint badge — appears on top-right of landscape screenshots */}
      <div aria-hidden="true" className="pointer-events-none absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex items-center gap-1.5 bg-black/55 backdrop-blur-sm text-white text-[11px] sm:text-xs font-medium rounded-full pl-2 pr-2.5 py-1 shadow-md opacity-90 sm:opacity-70 sm:group-hover:opacity-100 transition-opacity" data-id="element-1172">
        <ZoomInIcon size={13} strokeWidth={2.2} data-id="element-1173" />
        <span className="hidden sm:inline" data-id="element-1174">Нажмите для увеличения</span>
      </div>
    </div>;
}
// ─── Clickable media helpers ────────────────────────────────────────────
function ClickableImage({
  src,
  alt,
  onOpen,
  className
}: {
  src: string;
  alt: string;
  onOpen: (src: string) => void;
  className?: string;
}) {
  return <img src={src} alt={alt} onClick={() => onOpen(src)} className={`${className ?? ''} cursor-zoom-in transition-opacity hover:opacity-90`} data-id="element-1175" />;
}
function ClickableVideo({
  src,
  onOpen,
  className
}: {
  src: string;
  onOpen: (src: string) => void;
  className?: string;
}) {
  return <div onClick={() => onOpen(src)} className="cursor-zoom-in group/video relative" data-id="element-1176">
      <video src={src} autoPlay loop muted playsInline className={`${className ?? ''} transition-opacity group-hover/video:opacity-90 pointer-events-none`} data-id="element-1177" />
    </div>;
}
// ─── Coming Soon placeholder for pains without a full solution page ──────
function ComingSoonPage({
  painId
}: {
  painId: string;
}) {
  // Try to find the parent category + pain text for nicer context
  let painText = '';
  let categoryTitle = '';
  let categoryIcon = '';
  for (const cat of CATEGORIES) {
    const found = cat.pains.find(p => p.id === painId);
    if (found) {
      painText = found.text;
      categoryTitle = cat.title;
      categoryIcon = cat.icon;
      break;
    }
  }
  return <div className="min-h-screen bg-[#FAFAFA]" style={{
    animation: 'fadeIn 200ms ease-out'
  }} data-id="element-1178">
      <Navbar data-id="element-1179" />
      <StickyBackBar data-id="element-1180" />

      <section className="relative pt-10 sm:pt-14 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-id="element-1181">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(124,77,255,0.12) 0%, transparent 65%)'
      }} data-id="element-1182" />

        <div className="relative max-w-3xl mx-auto" data-id="element-1183">
          {categoryTitle && <div className="flex items-center gap-2 mb-5" data-id="element-1184">
              <span className="text-2xl" data-id="element-1185">{categoryIcon}</span>
              <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#7C4DFF] bg-[#EDE7F6] px-3 py-1.5 rounded-full" data-id="element-1186">
                {categoryTitle}
              </span>
            </div>}

          {painText && <h1 className="text-3xl sm:text-4xl lg:text-[44px] font-bold text-gray-900 leading-[1.15] tracking-tight mb-6" data-id="element-1187">
              {painText}
            </h1>}

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-7 sm:p-10 mt-8" data-id="element-1188">
            <div className="flex items-center gap-2 mb-4" data-id="element-1189">
              <span className="text-xs font-bold tracking-widest uppercase text-[#7C4DFF] bg-[#EDE7F6] px-2.5 py-1 rounded-full" data-id="element-1190">
                Скоро
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 tracking-tight" data-id="element-1191">
              Подробная страница в работе
            </h2>
            <p className="text-[15px] text-gray-600 leading-relaxed mb-7" data-id="element-1192">
              Мы готовим детальный разбор именно этого сценария. А пока — самый
              быстрый путь увидеть решение это короткое демо под ваш садик. На
              нём пройдёмся именно по этой ситуации.
            </p>

            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 bg-[#7C4DFF] text-white font-semibold text-base px-7 py-3.5 rounded-full shadow-lg hover:bg-[#651FFF] hover:-translate-y-0.5 transition-all duration-200" data-id="element-1193">
              <CalendarIcon size={17} strokeWidth={2.2} data-id="element-1194" />
              Запросить демо под мой садик
            </a>
            <p className="text-xs text-gray-400 mt-3" data-id="element-1195">
              Бесплатно · Без обязательств · 30 минут
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-1196" />
    </div>;
}